import axios from "axios";
import { toast } from "sonner";

// Use relative URLs in browser (same-origin, no cross-site cookie issues on mobile)
// Kubernetes ingress routes /api/* to backend automatically
const baseURL = typeof window !== "undefined" ? "" : (process.env.REACT_APP_BACKEND_URL || "");

const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 20000, // globální timeout 20 s → žádné volání nevisí donekonečna (jinak nekonečné načítání při zaseknutém serveru)
  headers: { "Content-Type": "application/json" }
});

// De-duplicate concurrent refresh-token calls so 3 parallel 401s don't
// fire 3 separate POST /auth/refresh requests (causing race conditions
// where some retries fire before the cookie is updated).
let refreshPromise = null;

// Cold-start auto-retry for 5xx / Cloudflare gateway errors.
// Emergent containers occasionally cold-start in the background — without
// this, a single 520 in an unattended API call would force the user to
// reload and log back in. With it, the call is transparently retried.
const COLD_RETRY_STATUSES = new Set([502, 504, 520, 521, 522, 523, 524]);
const COLD_RETRY_MAX = 3;
const COLD_RETRY_DELAYS = [1500, 4000, 8000];

const isColdRetryable = (error) => {
  // Volání, které si explicitně řekne `noColdRetry`, se nikdy neopakuje —
  // jinak jeden zaseknutý endpoint drží stránku v „Načítám…" i 90 sekund
  // (20 s timeout × 3 pokusy + prodlevy).
  if (error?.config?.noColdRetry) return false;
  // Skip retries on auth endpoints (login has its own dedicated retry budget)
  const url = error?.config?.url || "";
  if (url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh")) return false;
  // No response (network error / timeout) → retry
  if (!error?.response) return true;
  return COLD_RETRY_STATUSES.has(error.response.status);
};

api.interceptors.response.use(
  res => res,
  async error => {
    const original = error.config;
    // ── 1) 5xx / Cloudflare cold-start: transparent retry ─────────────
    if (original && !original._coldRetry && isColdRetryable(error)) {
      original._coldRetry = 0;
    }
    if (original && original._coldRetry != null && original._coldRetry < COLD_RETRY_MAX && isColdRetryable(error)) {
      const wait = COLD_RETRY_DELAYS[original._coldRetry] || 5000;
      original._coldRetry += 1;
      await new Promise(r => setTimeout(r, wait));
      return api(original);
    }
    const isAuthCall = original?.url?.includes("/auth/login")
      || original?.url?.includes("/auth/register")
      || original?.url?.includes("/auth/refresh");
    if (error.response?.status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios.post("/api/auth/refresh", {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }
    // Centrální UX pro úsporný režim AI — každý 503 z AI kill-switche zobrazí jednotnou hlášku.
    const detail = error.response?.data?.detail;
    if (error.response?.status === 503 && typeof detail === "string" && detail.includes("dočasně vypnutá")) {
      toast.info(detail, { id: "ai-disabled" });
    }
    return Promise.reject(error);
  }
);

export default api;
